import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Can } from './auth/Can.js';
import { ProtectedRoute } from './auth/ProtectedRoute.js';
import { AppShell } from './components/app-shell/AppShell.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { BankReconcilePage } from './pages/banking/BankReconcile.js';
import { BankRulesPage } from './pages/banking/BankRules.js';
import { BankTransactionsPage } from './pages/banking/BankTransactions.js';
import { Accounts } from './pages/bookkeeping/Accounts.js';
import { AtsPage } from './pages/bookkeeping/Ats.js';
import { BalanceSheet } from './pages/bookkeeping/BalanceSheet.js';
import { Customers } from './pages/bookkeeping/Customers.js';
import { F103Page } from './pages/bookkeeping/F103.js';
import { F104Page } from './pages/bookkeeping/F104.js';
import { GeneralLedger } from './pages/bookkeeping/GeneralLedger.js';
import { IncomeStatement } from './pages/bookkeeping/IncomeStatement.js';
import { Items } from './pages/bookkeeping/Items.js';
import { AdjustmentForm } from './pages/bookkeeping/inventory/Adjustment.js';
import { AdjustmentsPage } from './pages/bookkeeping/inventory/Adjustments.js';
import { KardexPage } from './pages/bookkeeping/inventory/Kardex.js';
import { StockPage } from './pages/bookkeeping/inventory/Stock.js';
import { WarehousesPage } from './pages/bookkeeping/inventory/Warehouses.js';
import { JournalEntries } from './pages/bookkeeping/JournalEntries.js';
import { JournalEntry } from './pages/bookkeeping/JournalEntry.js';
import { BillForm } from './pages/bookkeeping/purchases/Bill.js';
import { Bills } from './pages/bookkeeping/purchases/Bills.js';
import { CheckForm } from './pages/bookkeeping/purchases/Check.js';
import { Checks } from './pages/bookkeeping/purchases/Checks.js';
import { ExpenseForm } from './pages/bookkeeping/purchases/Expense.js';
import { Expenses } from './pages/bookkeeping/purchases/Expenses.js';
import { LiquidacionCompraPage } from './pages/bookkeeping/purchases/LiquidacionCompra.js';
import { PayBillForm } from './pages/bookkeeping/purchases/PayBill.js';
import { PayBills } from './pages/bookkeeping/purchases/PayBills.js';
import { CreditMemoForm } from './pages/bookkeeping/sales/CreditMemo.js';
import { CreditMemos } from './pages/bookkeeping/sales/CreditMemos.js';
import { GuiaRemisionPage } from './pages/bookkeeping/sales/GuiaRemision.js';
import { InvoiceForm } from './pages/bookkeeping/sales/Invoice.js';
import { Invoices } from './pages/bookkeeping/sales/Invoices.js';
import { NotaDebitoPage } from './pages/bookkeeping/sales/NotaDebito.js';
import { ReceivePaymentForm } from './pages/bookkeeping/sales/ReceivePayment.js';
import { ReceivePayments } from './pages/bookkeeping/sales/ReceivePayments.js';
import { SalesReceiptForm } from './pages/bookkeeping/sales/SalesReceipt.js';
import { SalesReceipts } from './pages/bookkeeping/sales/SalesReceipts.js';
import { TaxRates } from './pages/bookkeeping/sales/TaxRates.js';
import { WithholdingRates } from './pages/bookkeeping/sales/WithholdingRates.js';
import { TrialBalance } from './pages/bookkeeping/TrialBalance.js';
import { Vendors } from './pages/bookkeeping/Vendors.js';
import { CreateOrg } from './pages/CreateOrg.js';
import { Dashboard } from './pages/Dashboard.js';
import { ForgotPassword } from './pages/ForgotPassword.js';
import { Login } from './pages/Login.js';
import { MfaSetup } from './pages/MfaSetup.js';
import { MfaVerify } from './pages/MfaVerify.js';
import { BusinessInfo } from './pages/onboarding/BusinessInfo.js';
import { FiscalData } from './pages/onboarding/FiscalData.js';
import { Logo } from './pages/onboarding/Logo.js';
import { Timezone } from './pages/onboarding/Timezone.js';
import { Wizard } from './pages/onboarding/Wizard.js';
import { ResetPassword } from './pages/ResetPassword.js';
import { SelectOrg } from './pages/SelectOrg.js';
import { Signup } from './pages/Signup.js';
import {
  EmissionPointsPage,
  Invitations,
  Members,
  Profile,
  Security,
  SriCertificatePage,
} from './pages/settings/index.js';

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/mfa/setup" element={<MfaSetup />} />
        <Route path="/mfa/verify" element={<MfaVerify />} />
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/app" element={<Dashboard />} />
          <Route path="/app/onboarding/create-org" element={<CreateOrg />} />
          <Route path="/app/select-org" element={<SelectOrg />} />
          <Route path="/app/bookkeeping/accounts" element={<Accounts />} />
          <Route path="/app/bookkeeping/customers" element={<Customers />} />
          <Route path="/app/bookkeeping/vendors" element={<Vendors />} />
          <Route path="/app/bookkeeping/items" element={<Items />} />
          <Route path="/app/bookkeeping/journal-entries" element={<JournalEntries />} />
          <Route path="/app/bookkeeping/journal-entries/new" element={<JournalEntry />} />
          <Route path="/app/bookkeeping/journal-entries/:id" element={<JournalEntry />} />
          <Route
            path="/app/banking"
            element={
              <Can I="read" a="Account">
                <BankTransactionsPage />
              </Can>
            }
          />
          <Route
            path="/app/banking/rules"
            element={
              <Can I="read" a="Account">
                <BankRulesPage />
              </Can>
            }
          />
          <Route
            path="/app/banking/reconcile"
            element={
              <Can I="read" a="Account">
                <BankReconcilePage />
              </Can>
            }
          />
          <Route path="/app/bookkeeping/sales/invoices" element={<Invoices />} />
          <Route path="/app/bookkeeping/sales/invoices/new" element={<InvoiceForm />} />
          <Route path="/app/bookkeeping/sales/invoices/:id" element={<InvoiceForm />} />
          <Route path="/app/bookkeeping/sales/sales-receipts" element={<SalesReceipts />} />
          <Route path="/app/bookkeeping/sales/sales-receipts/new" element={<SalesReceiptForm />} />
          <Route path="/app/bookkeeping/sales/sales-receipts/:id" element={<SalesReceiptForm />} />
          <Route path="/app/bookkeeping/sales/receive-payments" element={<ReceivePayments />} />
          <Route
            path="/app/bookkeeping/sales/receive-payments/new"
            element={<ReceivePaymentForm />}
          />
          <Route
            path="/app/bookkeeping/sales/receive-payments/:id"
            element={<ReceivePaymentForm />}
          />
          <Route path="/app/bookkeeping/sales/tax-rates" element={<TaxRates />} />
          <Route path="/app/bookkeeping/sales/withholding-rates" element={<WithholdingRates />} />
          <Route path="/app/bookkeeping/purchases/bills" element={<Bills />} />
          <Route path="/app/bookkeeping/purchases/bills/new" element={<BillForm />} />
          <Route path="/app/bookkeeping/purchases/bills/:id" element={<BillForm />} />
          <Route path="/app/bookkeeping/purchases/expenses" element={<Expenses />} />
          <Route path="/app/bookkeeping/purchases/expenses/new" element={<ExpenseForm />} />
          <Route path="/app/bookkeeping/purchases/expenses/:id" element={<ExpenseForm />} />
          <Route path="/app/bookkeeping/purchases/pay-bills" element={<PayBills />} />
          <Route path="/app/bookkeeping/purchases/pay-bills/new" element={<PayBillForm />} />
          <Route path="/app/bookkeeping/purchases/pay-bills/:id" element={<PayBillForm />} />
          <Route path="/app/bookkeeping/purchases/checks" element={<Checks />} />
          <Route path="/app/bookkeeping/purchases/checks/new" element={<CheckForm />} />
          <Route path="/app/bookkeeping/purchases/checks/:id" element={<CheckForm />} />
          <Route
            path="/app/bookkeeping/purchases/liquidacion-compra/new"
            element={<LiquidacionCompraPage />}
          />
          <Route
            path="/app/bookkeeping/purchases/liquidacion-compra/:id"
            element={<LiquidacionCompraPage />}
          />
          <Route path="/app/bookkeeping/sales/credit-memos" element={<CreditMemos />} />
          <Route path="/app/bookkeeping/sales/credit-memos/new" element={<CreditMemoForm />} />
          <Route path="/app/bookkeeping/sales/credit-memos/:id" element={<CreditMemoForm />} />
          <Route path="/app/bookkeeping/sales/nota-debito/new" element={<NotaDebitoPage />} />
          <Route path="/app/bookkeeping/sales/nota-debito/:id" element={<NotaDebitoPage />} />
          <Route path="/app/bookkeeping/sales/guia-remision/new" element={<GuiaRemisionPage />} />
          <Route path="/app/bookkeeping/sales/guia-remision/:id" element={<GuiaRemisionPage />} />
          <Route path="/app/bookkeeping/inventory/warehouses" element={<WarehousesPage />} />
          <Route path="/app/bookkeeping/inventory/adjustments" element={<AdjustmentsPage />} />
          <Route path="/app/bookkeeping/inventory/adjustments/new" element={<AdjustmentForm />} />
          <Route path="/app/bookkeeping/inventory/adjustments/:id" element={<AdjustmentForm />} />
          <Route path="/app/bookkeeping/inventory/kardex" element={<KardexPage />} />
          <Route path="/app/bookkeeping/inventory/stock" element={<StockPage />} />
          <Route path="/app/bookkeeping/reports/trial-balance" element={<TrialBalance />} />
          <Route path="/app/bookkeeping/reports/income-statement" element={<IncomeStatement />} />
          <Route path="/app/bookkeeping/reports/balance-sheet" element={<BalanceSheet />} />
          <Route path="/app/bookkeeping/reports/general-ledger" element={<GeneralLedger />} />
          <Route path="/app/bookkeeping/reports/ats" element={<AtsPage />} />
          <Route path="/app/bookkeeping/reports/f104" element={<F104Page />} />
          <Route path="/app/bookkeeping/reports/f103" element={<F103Page />} />
          <Route path="/app/settings/profile" element={<Profile />} />
          <Route path="/app/settings/security" element={<Security />} />
          <Route path="/app/settings/members" element={<Members />} />
          <Route path="/app/settings/invitations" element={<Invitations />} />
          <Route path="/app/settings/sri-certificate" element={<SriCertificatePage />} />
          <Route path="/app/settings/emission-points" element={<EmissionPointsPage />} />
          <Route path="/app/onboarding/wizard" element={<Wizard />}>
            <Route path="business" element={<BusinessInfo />} />
            <Route path="fiscal" element={<FiscalData />} />
            <Route path="timezone" element={<Timezone />} />
            <Route path="logo" element={<Logo />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
